import type { AccessModel, Config } from '../../../types/config';
import type {
  ChangePassword,
  ChangePasswordWithOldPassword,
  DeleteAccount,
  ExportAccountData,
  GetCaptureStatus,
  GetCustomerConsents,
  GetPublisherConsents,
  Login,
  LoginExternal,
  Register,
  ResetPassword,
  UpdateCaptureAnswers,
  UpdateCustomerConsents,
  Logout,
  GetAuthData,
  UpdateCustomer,
  UpdateWatchHistory,
  UpdateFavorites,
  GetWatchHistory,
  GetFavorites,
  GetUser,
  SetAuthData,
  GenerateCode,
  AuthorizeCode,
  CheckCode,
  GetExternalLoginProviders,
  GetCustomerPortalUrl,
  ConfirmLogin,
} from '../../../types/account';

export type AccountServiceFeatures = {
  readonly canUpdateEmail: boolean;
  readonly canSupportEmptyFullName: boolean;
  readonly canChangePasswordWithOldPassword: boolean;
  readonly canRenewSubscription: boolean;
  readonly canExportAccountData: boolean;
  readonly canDeleteAccount: boolean;
  readonly canUpdatePaymentMethod: boolean;
  readonly canShowReceipts: boolean;
  readonly canEditAboutYou: boolean;
  readonly canEditSecurity: boolean;
  readonly canEditTermsAndTracking: boolean;
  readonly canEditRegistrationDetails: boolean;
  readonly watchListSizeLimit: number;
  readonly paymentsPage: 'in-app' | 'hosted' | 'none';
  readonly landingEnabled?: boolean;
  /**
   * This controls the device flow behavior for routing to the verification page:
   * `in-app` -> Opens a page on the webapp `{base URL}/tv`
   * `hosted` -> Opens the URL as external URL
   */
  readonly verificationPage?: 'in-app' | 'hosted';
  hideSubscriptionSection?: boolean;
};

export default abstract class AccountService {
  readonly features: AccountServiceFeatures;

  abstract accessModel: AccessModel;
  abstract svodOfferIds: string[];
  abstract sandbox: boolean;

  constructor(features: AccountServiceFeatures) {
    this.features = features;
  }

  abstract initialize: (config: Config, url: string, logoutCallback: () => Promise<void>) => Promise<void>;

  abstract getAuthData: GetAuthData;

  abstract login: Login;

  abstract confirmLogin?: ConfirmLogin;

  abstract loginExternal?: LoginExternal;

  abstract register: Register;

  abstract logout: Logout;

  abstract getUser: GetUser;

  abstract getPublisherConsents: GetPublisherConsents;

  abstract getCustomerConsents: GetCustomerConsents;

  abstract updateCustomerConsents: UpdateCustomerConsents;

  abstract getCaptureStatus: GetCaptureStatus;

  abstract updateCaptureAnswers: UpdateCaptureAnswers;

  abstract resetPassword: ResetPassword;

  abstract changePasswordWithResetToken: ChangePassword;

  abstract changePasswordWithOldPassword: ChangePasswordWithOldPassword;

  abstract updateCustomer: UpdateCustomer;

  abstract updateWatchHistory: UpdateWatchHistory;

  abstract updateFavorites: UpdateFavorites;

  abstract getWatchHistory: GetWatchHistory;

  abstract getFavorites: GetFavorites;

  abstract exportAccountData?: ExportAccountData;

  abstract deleteAccount?: DeleteAccount;

  abstract getExternalLoginProviders?: GetExternalLoginProviders;

  abstract getCustomerPortalUrl?: GetCustomerPortalUrl;

  /**
   * VD Private code below
   */

  abstract setAuthData?: SetAuthData;
  abstract generateCode?: GenerateCode;
  abstract authorizeCode?: AuthorizeCode;
  abstract checkCode?: CheckCode;
}
